import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  ConfirmationDialog,
  Input,
  Label,
  Progress,
  RadioGroup,
  RadioGroupItem,
  Skeleton,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import {
  AlertTriangle,
  ChevronDown,
  FileDown,
  Loader2,
  Pause,
  Play,
  RefreshCw,
  RotateCcw,
  Trash2,
  Upload,
} from 'lucide-react';
import { PermissionSlug } from '../constants/featureFlags';
import { useStore } from '../store';
import { formatDateTimeInPT } from '../utils/dateUtils';
import {
  getUserFacingApiMessage,
  getUserFacingApiMessageAsync,
} from '../utils/apiErrorDetail';
import { downloadBlobAsFile } from '../utils/downloadFile';
import {
  downloadImporterRosterStatus,
  getImporterQueue,
  getImporterSettings,
  pauseImporterLane,
  resetAgencyImport,
  resetImporterLaneBreaker,
  resumeImporterLane,
  retryImporterLaneFailures,
  retryImporterQueueRow,
  setImporterCapturePaused,
  setImporterChartsPerLogin,
  setImporterMaxConcurrentLanes,
  uploadImporterRosterPage,
  MAX_CHARTS_PER_LOGIN,
  MAX_CONCURRENT_LANES,
  MIN_CHARTS_PER_LOGIN,
  MIN_CONCURRENT_LANES,
  type EmptyReferralPolicy,
  type ImporterCredentialLane,
  type ImporterLane,
  type ImporterQueue,
  type ImporterQueueRow,
  type ImporterResetResult,
  type ImporterRetryFailedResult,
  type ImporterRosterPrintMode,
  type ImporterRosterResult,
  type ImporterRosterRowNote,
  type ImporterRosterStatusReport,
  type ImporterSettings,
  type ImporterQueueStatusFilter,
} from '../services/importer';

// The page exists to be watched while a capture drains, so it refreshes itself
// — quickly while it is the tab being read, and at a trickle once it is not.
// A capture of 24,000 charts is left open for hours, and a background tab
// polling every 5 seconds is thousands of requests nobody looks at.
const FOCUSED_REFRESH_INTERVAL_MS = 5_000;
const HIDDEN_REFRESH_INTERVAL_MS = 30_000;

// Finished charts per page. The queue behind this page runs to five figures,
// so the table is a window onto it and "Show older" walks the cursor back.
const QUEUE_PAGE_SIZE = 100;

type QueueViewFilter = 'all' | ImporterQueueStatusFilter;

const QUEUE_VIEW_FILTERS: {
  value: QueueViewFilter;
  label: string;
  description: string;
}[] = [
  {
    value: 'all',
    label: 'All charts',
    description:
      'Queued and importing first, then the charts that finished most recently.',
  },
  {
    value: 'failed',
    label: 'Failed only',
    description:
      'Every chart that failed, newest first. Use Retry on each row or Retry failed on a login card to reimport.',
  },
  {
    value: 'completed',
    label: 'Imported only',
    description: 'Charts that finished importing, newest first.',
  },
];

// How many lane cards stand above the queue before the rest are folded away.
// Two rows on a wide screen: enough to watch a capture, few enough that the
// queue is still the next thing on the page rather than a scroll away.
const VISIBLE_LANES = 6;

// What the server counts as still-to-do, mirroring `_LIVE_STATUSES` in
// `queue.py`. Used to tell a paged live half from a complete one.
const LIVE_STATUSES = new Set(['queued', 'running']);

// How much of the per-row detail to put on the page. The full list goes to the
// CSV: a roster that collapses seven thousand episodes is a spreadsheet to work
// through, not something to scroll past the queue.
const VISIBLE_NOTES = 50;

const ROSTER_UPLOAD_FAILED =
  'Could not read that roster. Please check the file and try again.';

const ROSTER_STATUS_FAILED =
  'Could not build the status report. Please check the file and try again.';

const ROW_RETRY_FAILED = 'Could not retry that chart. Please try again.';

const LANE_RETRY_FAILED =
  'Could not retry the failed charts. Please try again.';

const QUEUE_LOAD_FAILED = 'Unable to load the import queue. Please try again.';

const CHARTS_PER_LOGIN_SAVE_FAILED =
  'Could not save charts per login. Please try again.';
const LANE_COUNT_SAVE_FAILED =
  'Could not change the number of logins. Please try again.';

const LANE_PAUSE_FAILED = 'Could not pause that login. Please try again.';

const LANE_RESUME_FAILED = 'Could not resume that login. Please try again.';

const CAPTURE_PAUSE_FAILED = 'Could not pause the import. Please try again.';

const CAPTURE_RESUME_FAILED = 'Could not resume the import. Please try again.';

const LANE_RESET_FAILED =
  'Could not try that login again. Please try again in a moment.';

const AGENCY_RESET_FAILED =
  "Could not reset this agency's import. Please try again.";

/**
 * One vocabulary for one state, everywhere on the page: the lane badges, the
 * queue rows and the roster tiles all call a waiting chart "queued".
 */
const STATUS_LABELS: Record<
  string,
  {
    label: string;
    variant: 'default' | 'secondary' | 'destructive' | 'outline';
  }
> = {
  queued: { label: 'Queued', variant: 'secondary' },
  running: { label: 'Importing', variant: 'default' },
  completed: { label: 'Imported', variant: 'outline' },
  failed: { label: 'Failed', variant: 'destructive' },
  canceled: { label: 'Canceled', variant: 'outline' },
};

function formatEta(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round((seconds % 3600) / 60);
  if (hours === 0) return `${minutes}m`;
  // "1h 0m" is two units saying what one says.
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}m`;
}

/**
 * How long a chart took once it has both a start and an end.
 *
 * Seconds stay on the page because a capture is planned against per-chart
 * cost, and rounding that to a minute hides the delta the Ended column is
 * there to measure. Hours drop the seconds: a two-hour chart is not being
 * timed to the second.
 */
function formatElapsedSeconds(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainder = seconds % 60;
  if (hours > 0) {
    if (minutes === 0) return `${hours}h`;
    return `${hours}h ${minutes}m`;
  }
  if (minutes > 0) {
    if (remainder === 0) return `${minutes}m`;
    return `${minutes}m ${remainder}s`;
  }
  return `${remainder}s`;
}

function elapsedLabel(row: ImporterQueueRow): string {
  if (!row.claimed_at || !row.ended_at) return '—';
  const start = Date.parse(row.claimed_at);
  const end = Date.parse(row.ended_at);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) {
    return '—';
  }
  return formatElapsedSeconds(Math.round((end - start) / 1000));
}

/**
 * The lane's measured pace, or why there isn't one.
 *
 * The server sends a null rate rather than a modelled one, so a lane that has
 * not moved says so instead of showing a number nobody can act on.
 */
function chartsPerHour(rate: number): string {
  return `${Math.round(rate * 10) / 10}/hr`;
}

/**
 * The pool's measured pace — every login here added together — and what that
 * pace means for the charts still queued.
 *
 * "To go" rather than "left": the card already says how many charts are left,
 * and that number counts the failed ones while this estimate cannot. Nobody
 * is coming for a refused chart, so no rate predicts when it lands.
 */
function pace(lane: ImporterLane): string {
  if (lane.charts_per_hour === null) {
    return `No rate yet — nothing finished in the last ${lane.rate_window_minutes} min`;
  }
  const rate = `${chartsPerHour(lane.charts_per_hour)} across every login`;
  if (lane.eta_seconds === null) return rate;
  return `${rate} · about ${formatEta(lane.eta_seconds)} to go`;
}

/**
 * A login the operator can tell one card from another by, when it has no name.
 *
 * Roger Docs stores a label against every login and the queue now carries it,
 * so this is the fallback for a login stored without one — not the normal
 * case. The id's tail and not its head: these ids open with a creation
 * timestamp, so two logins stored the same afternoon share their first
 * characters and differ only in their last.
 */
function credentialName(credentialId: string): string {
  return `login ${credentialId.slice(-6)}`;
}

/** The login shown against one chart.
 *
 * A pooled row belongs to no login until one claims it, so the row's own
 * credential is the only honest answer once it has one. Falling back to the
 * lane's name is right only while the row is unclaimed: before pooling the
 * lane's credential WAS the row's, and reusing it afterwards put one login's
 * name on another login's work.
 */
function rowLogin(
  row: ImporterQueueRow,
  laneNames: Map<string, string>,
  loginNames: Map<string, string>
): string | undefined {
  if (row.credential_id) {
    // The same name the cards use, so one login does not read as two things
    // on one page.
    const login =
      loginNames.get(row.credential_id) ?? credentialName(row.credential_id);
    return `${row.source_agency_id} \u00b7 ${login}`;
  }
  return laneNames.get(row.queue_key);
}

/** What Roger Docs calls one login, or the least-bad stand-in for its name. */
function credentialLaneName(credential: ImporterCredentialLane): string {
  if (credential.credential_label) return credential.credential_label;
  if (credential.credential_id) return credentialName(credential.credential_id);
  // Nobody named a login on this lane; Roger Docs resolves one when it
  // scrapes, so there is no account here to put a name to.
  return 'Any stored login';
}

/**
 * What one login is doing, when no state badge already says.
 *
 * `running` is 0 or 1 by construction — one chart per login is the invariant
 * the whole queue exists to keep — so it reads as a state, not a count.
 */
function credentialActivity(credential: ImporterCredentialLane): string {
  const doing =
    credential.running > 0 ? 'Importing a chart' : 'Waiting for a chart';
  if (credential.charts_per_hour === null) return doing;
  return `${doing} · ${chartsPerHour(credential.charts_per_hour)}`;
}

/**
 * What one card is a card of: the pool, which is one agency's charts.
 *
 * Not a login. Charts are pooled and any login may take any of them, so the
 * logins are a list inside the card and are named there once. Putting one of
 * them in the title as well made a pool of three logins read as a single
 * login's work — and named the wrong one, because a pool has no single login.
 *
 * The exception is a lane pinned to one credential, which is what the queue
 * looked like before pooling. Those drain rather than being rewritten, so
 * while any are left they say which login they are pinned to.
 */
function laneLabel(lane: ImporterLane): string {
  if (!lane.source_agency_id) return lane.queue_key;
  if (lane.credential_lanes.length > 0 || !lane.credential_id) {
    return lane.source_agency_id;
  }
  const login = lane.credential_label ?? credentialName(lane.credential_id);
  return `${lane.source_agency_id} · ${login}`;
}

function laneTotal(lane: ImporterLane): number {
  const { queued, running, completed, failed, canceled } = lane.counts;
  return queued + running + completed + failed + canceled;
}

/**
 * Charts still to do — which a failed one is.
 *
 * The server's `remaining` counts what the sequencer will pick up by itself,
 * so a refused chart is not in it. That is right for the sequencer and wrong
 * for the operator reading the card: nobody is coming for that chart, and a
 * lane of nothing but failures would say "0 left" with nothing imported.
 * Counted this way, imported + left + canceled is the total.
 */
function laneLeft(lane: ImporterLane): number {
  const { queued, running, failed } = lane.counts;
  return queued + running + failed;
}

/**
 * Where a lane sits in the list: paused first, then lanes with failures, then
 * lanes still working, then the ones that are done.
 *
 * The rank is deliberately coarse. Sorting by counts would move a card under
 * the pointer every time a chart finished, and only these four states change
 * what the operator should do next.
 */
function laneRank(lane: ImporterLane): number {
  if (lane.breaker.is_open) return 0;
  // A pool whose breaker is shut can still hold a login that is stopped: the
  // pool's breaker opens only when every login is refused at once.
  if (
    lane.credential_lanes.some(
      credential => credential.breaker.is_open || credential.is_paused
    )
  )
    return 0;
  if (lane.counts.failed > 0) return 1;
  if (lane.remaining > 0) return 2;
  return 3;
}

function patientRef(row: ImporterQueueRow): string {
  const ref = row.mrn ?? row.patient_key ?? '—';
  // A print retry is not a chart: it asks again for the print views an
  // earlier import of this patient lost (EMR-2373).
  if (row.print_retry_task_count == null) return ref;
  const prints = row.print_retry_task_count === 1 ? 'print' : 'prints';
  return `${ref} · retrying ${row.print_retry_task_count} ${prints} (try ${row.print_retry_attempt ?? 1})`;
}

/**
 * Every loaded page's rows, in order, each id only once.
 *
 * Retrying a chart moves it out of the finished half and back into the live
 * rows of the first page, where a page loaded earlier is still holding the
 * finished copy. First occurrence wins, so the row that shows is the fresh one.
 */
function pagedRows(pages: ImporterQueue[]): ImporterQueueRow[] {
  const seen = new Set<string>();
  const rows: ImporterQueueRow[] = [];
  for (const page of pages) {
    for (const row of page.items) {
      if (seen.has(row.id)) continue;
      seen.add(row.id);
      rows.push(row);
    }
  }
  return rows;
}

/**
 * What a lane's retry did, in the operator's terms.
 *
 * Every count the server reports is said out loud rather than folded into the
 * headline number: a press that reopened three of eleven charts and left eight
 * alone is not a press that worked, and the two skip reasons are the only
 * things that say which button to reach for next.
 */
function resetSummary(totals: ImporterResetResult): string {
  const parts = [
    `${totals.canceled_jobs} ${totals.canceled_jobs === 1 ? 'job' : 'jobs'} canceled`,
    `${totals.queue_rows_deleted} queue ${totals.queue_rows_deleted === 1 ? 'row' : 'rows'} removed`,
    `${totals.patients_deleted} imported ${totals.patients_deleted === 1 ? 'patient' : 'patients'} deleted`,
  ];
  if (totals.patients_remaining > 0) {
    parts.push(
      `${totals.patients_remaining} imported ${totals.patients_remaining === 1 ? 'patient' : 'patients'} left (Roger work on chart)`
    );
  }
  return parts.join(', ') + '.';
}

function retrySummary(result: ImporterRetryFailedResult): string {
  const parts = [`Retried ${result.retried} of the ${result.examined} read.`];
  if (result.already_queued > 0) {
    parts.push(`${result.already_queued} already queued or importing.`);
  }
  if (result.canceled > 0) {
    parts.push(`${result.canceled} canceled — queue those patients again.`);
  }
  if (result.over_limit > 0) {
    parts.push(
      `${result.over_limit} still failed; press again for the next ${result.limit}.`
    );
  }
  if (result.retried > 0 && !result.sequencer_kicked) {
    parts.push('Importing starts within 5 minutes.');
  }
  return parts.join(' ');
}

/** One refused login, and the pool it was working. */
interface RefusedLogin {
  pool: string;
  credential: ImporterCredentialLane;
}

/**
 * Every login the source EMR has stopped, at the top of the page.
 *
 * A refused login is the one thing here worth interrupting for — it needs a
 * password fixed, and nothing on the page will fix itself — so it is read
 * before the cards rather than found inside one of them.
 *
 * Refusals only. An operator's pause is deliberate and already visible on the
 * card that was paused; alarming somebody about their own press would teach
 * them to ignore this banner.
 */
const RefusedLogins: React.FC<{ logins: RefusedLogin[] }> = ({ logins }) => (
  <Alert variant='destructive'>
    <AlertTriangle className='h-4 w-4' />
    <AlertTitle>
      {logins.length === 1
        ? '1 login was refused'
        : `${logins.length} logins were refused`}
    </AlertTitle>
    <AlertDescription className='space-y-1'>
      {logins.map(({ pool, credential }) => (
        <p key={credential.lane_key}>
          <span className='font-medium'>
            {pool} · {credentialLaneName(credential)}
          </span>{' '}
          —{' '}
          {credential.breaker.reason ??
            'The old EMR refused this login repeatedly.'}
          {credential.breaker.probe_after &&
            ` Tries again after ${formatDateTimeInPT(credential.breaker.probe_after)}.`}
        </p>
      ))}
    </AlertDescription>
  </Alert>
);

/**
 * What the number on screen will actually do, given the logins that exist.
 *
 * Asking for more lanes than Roger Docs has logins for is allowed and is not a
 * promise, so the card says which of the two numbers is the real one rather
 * than letting an operator keep raising a dial already at its ceiling.
 */
function laneCountNote(settings: ImporterSettings): string {
  const stored = settings.valid_credentials;
  // Not "none are stored": 0 is also what a Roger Docs this page could not
  // reach looks like, and the operator's next move is the same either way.
  if (stored === 0)
    return 'No working login is available, so nothing will import.';
  if (settings.is_paused) {
    return 'Queued charts wait until you resume. Nothing already importing is stopped.';
  }
  const stock = `You have ${stored} ${stored === 1 ? 'login' : 'logins'} stored`;
  if (settings.is_default) {
    return stored === 1
      ? `${stock}, and it is importing.`
      : `${stock}, and all of them are importing.`;
  }
  if (settings.max_concurrent_lanes > stored) {
    return `${stock}, so ${stored} will import.`;
  }
  return `${stock}.`;
}

/** The Logins card's first sentence, which the charts-per-login dial changes. */
function chartsPerLoginSentence(settings: ImporterSettings | null): string {
  const charts = settings?.charts_per_login ?? 1;
  return charts === 1
    ? 'Each login imports one chart at a time, so more logins import faster.'
    : `Each login imports up to ${charts} charts at a time, so more logins import faster.`;
}

/**
 * Sessions on one login that have been seen to work together. Past this,
 * nobody has tried, and the source EMR has not said what it allows.
 */
const PROVEN_SESSIONS_PER_LOGIN = 50;

/** How each chart uses its login, in the card's own words. */
function sessionsPerChartSentence(sessionsPerChart: number): string {
  if (sessionsPerChart === 1) {
    return 'Each chart is its own session on the login, so a login importing two charts is signed in twice.';
  }
  const alongside = sessionsPerChart - 1;
  const verb = alongside === 1 ? 'reads' : 'read';
  return `Each chart uses ${sessionsPerChart} sessions of the login: one reads the chart, and ${alongside} ${verb} its visits alongside.`;
}

/** The session count the typed number puts on each login, and whether it is tried. */
function sessionsPerLoginSentence(sessions: number): string {
  const count = `Up to ${sessions} ${sessions === 1 ? 'session' : 'sessions'} on each login.`;
  return sessions <= PROVEN_SESSIONS_PER_LOGIN
    ? `${count} Up to ${PROVEN_SESSIONS_PER_LOGIN} on one login has worked.`
    : `${count} More than ${PROVEN_SESSIONS_PER_LOGIN} has not been tried, and the source EMR has not said how many one login may hold. Go up one step at a time.`;
}

/**
 * How many charts one login may import at once.
 *
 * Each chart holds `sessions_per_chart` Kinnser sessions on the login, set
 * by deploy, so the card states the sessions the typed number means rather
 * than leaving the operator to multiply. Roger Docs keeps that many warm. Live
 * in both directions like the lane count, and equally unable to interrupt a
 * chart.
 */
const ChartsPerLogin: React.FC<{
  settings: ImporterSettings | null;
  hasFailed: boolean;
  isSaving: boolean;
  onSave: (charts: number) => void;
}> = ({ settings, hasFailed, isSaving, onSave }) => {
  const saved = settings?.charts_per_login ?? null;
  // Unknown until the settings load. Guessing 1 would state the wrong
  // session count on a deploy set higher, so nothing is said until then.
  const sessionsPerChart = settings?.sessions_per_chart ?? null;
  const [draft, setDraft] = useState('');

  useEffect(() => {
    if (saved !== null) setDraft(String(saved));
  }, [saved]);

  const wanted = Number(draft);
  const isValid =
    draft.trim() !== '' &&
    Number.isInteger(wanted) &&
    wanted >= MIN_CHARTS_PER_LOGIN &&
    wanted <= MAX_CHARTS_PER_LOGIN;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Charts per login</CardTitle>
        <CardDescription>
          {sessionsPerChart !== null &&
            `${sessionsPerChartSentence(sessionsPerChart)} `}
          Raising this starts the extra charts within 5 minutes. Lowering it
          waits for charts to finish — nothing already importing is stopped.
        </CardDescription>
      </CardHeader>
      <CardContent className='space-y-2'>
        {hasFailed ? (
          <p className='text-sm text-muted-foreground'>
            Could not load this setting. Press Refresh to try again.
          </p>
        ) : settings === null ? (
          <Skeleton className='h-9 w-full sm:w-64' />
        ) : (
          <>
            <div className='flex flex-col gap-3 sm:flex-row sm:items-end'>
              <div className='space-y-1'>
                <Label htmlFor='importer-charts-per-login'>
                  Charts per login
                </Label>
                <Input
                  id='importer-charts-per-login'
                  type='number'
                  inputMode='numeric'
                  min={MIN_CHARTS_PER_LOGIN}
                  max={MAX_CHARTS_PER_LOGIN}
                  value={draft}
                  disabled={isSaving}
                  onChange={event => setDraft(event.target.value)}
                  className='w-full sm:w-24'
                />
              </div>
              <Button
                size='sm'
                aria-label='Save charts per login'
                disabled={isSaving || !isValid || wanted === saved}
                onClick={() => onSave(wanted)}
              >
                {isSaving && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
                Save
              </Button>
            </div>
            <p className='text-sm text-muted-foreground'>
              {!isValid
                ? `Choose a number from ${MIN_CHARTS_PER_LOGIN} to ${MAX_CHARTS_PER_LOGIN}.`
                : sessionsPerChart !== null &&
                  sessionsPerLoginSentence(wanted * sessionsPerChart)}
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * How many logins the capture may work with at once.
 *
 * The dial is live in both directions and neither direction interrupts a
 * chart, which is the one thing an operator cannot tell by looking: lowering
 * it reads like an abort and is not one. That is said next to the control, not
 * only in the toast that follows it.
 */
const LaneCount: React.FC<{
  settings: ImporterSettings | null;
  hasFailed: boolean;
  isSaving: boolean;
  isTogglingPause: boolean;
  isResetting: boolean;
  onSave: (lanes: number) => void;
  onTogglePause: () => void;
}> = ({
  settings,
  hasFailed,
  isSaving,
  isTogglingPause,
  isResetting,
  onSave,
  onTogglePause,
}) => {
  const saved = settings?.max_concurrent_lanes ?? null;
  const [draft, setDraft] = useState('');

  // The server owns the number; the box only holds what is being typed into
  // it, so a save — or a Refresh — puts it back in step.
  useEffect(() => {
    if (saved !== null) setDraft(String(saved));
  }, [saved]);

  const wanted = Number(draft);
  const isValid =
    draft.trim() !== '' &&
    Number.isInteger(wanted) &&
    wanted >= MIN_CONCURRENT_LANES &&
    wanted <= MAX_CONCURRENT_LANES;
  const isPaused = settings?.is_paused === true;

  return (
    <Card>
      <CardHeader>
        <div className='flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between'>
          <div className='space-y-1.5'>
            <div className='flex flex-wrap items-center gap-2'>
              <CardTitle>Logins</CardTitle>
              {isPaused && (
                <Badge variant='secondary' className='whitespace-nowrap'>
                  Paused
                </Badge>
              )}
            </div>
            <CardDescription>
              {chartsPerLoginSentence(settings)} Raising this starts the extra
              logins within 5 minutes. Lowering it stops extra logins from
              starting another chart — a chart already importing finishes.
            </CardDescription>
          </div>
          {settings !== null && !hasFailed && (
            <Button
              size='sm'
              variant={isPaused ? 'default' : 'outline'}
              disabled={isTogglingPause || isResetting}
              onClick={onTogglePause}
              className='shrink-0 self-start'
            >
              {isTogglingPause ? (
                <Loader2 className='mr-1.5 h-3.5 w-3.5 animate-spin' />
              ) : isPaused ? (
                <Play className='mr-1.5 h-3.5 w-3.5' />
              ) : (
                <Pause className='mr-1.5 h-3.5 w-3.5' />
              )}
              {isPaused ? 'Resume import' : 'Pause import'}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className='space-y-2'>
        {hasFailed ? (
          <p className='text-sm text-muted-foreground'>
            Could not load this setting. Press Refresh to try again.
          </p>
        ) : settings === null ? (
          <Skeleton className='h-9 w-full sm:w-64' />
        ) : (
          <>
            <div className='flex flex-col gap-3 sm:flex-row sm:items-end'>
              <div className='space-y-1'>
                <Label htmlFor='importer-lane-count'>
                  How many import at once
                </Label>
                <Input
                  id='importer-lane-count'
                  type='number'
                  inputMode='numeric'
                  min={MIN_CONCURRENT_LANES}
                  max={MAX_CONCURRENT_LANES}
                  value={draft}
                  disabled={isSaving}
                  onChange={event => setDraft(event.target.value)}
                  className='w-full sm:w-24'
                />
              </div>
              <Button
                size='sm'
                disabled={isSaving || !isValid || wanted === saved}
                onClick={() => onSave(wanted)}
              >
                {isSaving && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
                Save
              </Button>
            </div>
            {/* How many logins exist is what makes a refused number make
                sense, so it is on the card rather than in the error that
                follows pressing Save. A number the operator can correct is
                said out loud for the same reason. */}
            <p
              className={
                isValid && settings.valid_credentials === 0
                  ? 'text-sm text-destructive'
                  : 'text-sm text-muted-foreground'
              }
            >
              {isValid
                ? laneCountNote(settings)
                : `Choose a number from ${MIN_CONCURRENT_LANES} to ${MAX_CONCURRENT_LANES}.`}
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * Everything the operator can do to one login, and the state that says which
 * of it is worth doing.
 *
 * A pause and a tripped breaker are shown as two separate things because they
 * are undone two separate ways — one by pressing Resume, the other by fixing
 * the password behind the login. Folding them into one "stopped" would tell
 * somebody their own press was the source EMR refusing their credential.
 */
const CredentialLaneRow: React.FC<{
  credential: ImporterCredentialLane;
  busy: boolean;
  onPause: () => void;
  onResume: () => void;
  onResetBreaker: () => void;
}> = ({ credential, busy, onPause, onResume, onResetBreaker }) => {
  const { breaker } = credential;
  // Why it stopped beats what it would otherwise be doing — and a stopped
  // login keeps its measured pace, because a login that was importing 4/hr
  // until an hour ago is a different problem from one that never worked.
  const activity = breaker.is_open
    ? `${breaker.reason ?? 'The source EMR refused this login repeatedly.'}${
        breaker.probe_after
          ? ` Tries again after ${formatDateTimeInPT(breaker.probe_after)}.`
          : ''
      }`
    : credential.is_paused
      ? 'Claiming nothing until you resume it.'
      : credentialActivity(credential);
  return (
    <li className='flex flex-wrap items-start justify-between gap-2 py-2'>
      <div className='min-w-0 space-y-0.5'>
        <div className='flex flex-wrap items-center gap-1.5'>
          <span className='text-sm font-medium'>
            {credentialLaneName(credential)}
          </span>
          {breaker.is_open && (
            <Badge variant='destructive' className='whitespace-nowrap'>
              Sign-in refused
            </Badge>
          )}
          {credential.is_paused && (
            <Badge variant='secondary' className='whitespace-nowrap'>
              Paused
            </Badge>
          )}
          {credential.running > 0 && !breaker.is_open && (
            <Badge variant='default' className='whitespace-nowrap'>
              Importing
            </Badge>
          )}
        </div>
        {/* One line, and it says the thing that changes what to do next: why
            it stopped beats what it would be doing if it hadn't. */}
        <p className='text-xs text-muted-foreground'>{activity}</p>
      </div>
      <div className='flex shrink-0 items-center gap-1'>
        {breaker.is_open && (
          <Button
            variant='outline'
            size='sm'
            disabled={busy}
            onClick={onResetBreaker}
          >
            Try now
          </Button>
        )}
        {credential.is_paused ? (
          <Button
            variant='outline'
            size='sm'
            disabled={busy}
            onClick={onResume}
          >
            <Play className='mr-1.5 h-3.5 w-3.5' />
            Resume
          </Button>
        ) : (
          <Button variant='ghost' size='sm' disabled={busy} onClick={onPause}>
            <Pause className='mr-1.5 h-3.5 w-3.5' />
            Pause
          </Button>
        )}
      </div>
    </li>
  );
};

const LaneCard: React.FC<{
  lane: ImporterLane;
  captureIsPaused: boolean;
  onRetryFailed: () => void;
  isRetrying: boolean;
  busyCredentialLane: string | null;
  onPause: (laneKey: string) => void;
  onResume: (laneKey: string) => void;
  onResetBreaker: (laneKey: string) => void;
}> = ({
  lane,
  captureIsPaused,
  onRetryFailed,
  isRetrying,
  busyCredentialLane,
  onPause,
  onResume,
  onResetBreaker,
}) => {
  const { queued, running, completed, failed, canceled } = lane.counts;
  const total = laneTotal(lane);
  const left = laneLeft(lane);
  return (
    <Card>
      <CardHeader>
        <div className='flex flex-wrap items-center gap-2'>
          <CardTitle className='text-base'>{laneLabel(lane)}</CardTitle>
          {/* The pool's breaker is open only when every login here is
              refused, so it is a different sentence from one login's — and
              from an operator's pause, which is nobody being refused at all. */}
          {lane.breaker.is_open && (
            <Badge variant='destructive' className='whitespace-nowrap'>
              {lane.credential_lanes.length > 1
                ? 'Every login refused'
                : 'Login refused'}
            </Badge>
          )}
          {captureIsPaused && (
            <Badge variant='secondary' className='whitespace-nowrap'>
              Paused
            </Badge>
          )}
        </div>
        {/* "Left" counts the failed charts, which the server's `remaining`
            does not: nobody is coming for a refused chart by itself, so a
            card that left it out could read "0 left" with nothing imported. */}
        <CardDescription>
          {completed} of {total} imported · {left} left
        </CardDescription>
      </CardHeader>
      <CardContent className='space-y-3'>
        <Progress
          value={total === 0 ? 0 : Math.round((completed / total) * 100)}
          aria-label={`${completed} of ${total} charts imported`}
        />
        {/* A count of zero is neutral: colour on this page means there is
            something to look at, so a lane with nothing failed must not put a
            red badge in front of an operator scanning a wall of cards. */}
        <div className='flex flex-wrap gap-2'>
          <Badge
            variant={queued > 0 ? 'secondary' : 'outline'}
            className='whitespace-nowrap'
          >
            {queued} queued
          </Badge>
          <Badge
            variant={running > 0 ? 'default' : 'outline'}
            className='whitespace-nowrap'
          >
            {running} importing
          </Badge>
          <Badge
            variant={failed > 0 ? 'destructive' : 'outline'}
            className='whitespace-nowrap'
          >
            {failed} failed
          </Badge>
          {canceled > 0 && (
            <Badge variant='outline' className='whitespace-nowrap'>
              {canceled} canceled
            </Badge>
          )}
        </div>
        <p className='text-sm text-muted-foreground'>{pace(lane)}</p>
        {/* The logins are the card's subject once there is more than one of
            them: the pool's own numbers say how the capture is going, and
            these say which login to go and fix. */}
        {lane.credential_lanes.length > 0 && (
          <div className='space-y-1 border-t pt-2'>
            <p className='text-xs font-medium text-muted-foreground'>
              {lane.credential_lanes.length === 1
                ? '1 login'
                : `${lane.credential_lanes.length} logins`}
            </p>
            <ul className='divide-y'>
              {lane.credential_lanes.map(credential => (
                <CredentialLaneRow
                  key={credential.lane_key}
                  credential={credential}
                  busy={busyCredentialLane === credential.lane_key}
                  onPause={() => onPause(credential.lane_key)}
                  onResume={() => onResume(credential.lane_key)}
                  onResetBreaker={() => onResetBreaker(credential.lane_key)}
                />
              ))}
            </ul>
          </div>
        )}
        {failed > 0 && (
          <Button
            variant='outline'
            size='sm'
            onClick={onRetryFailed}
            disabled={isRetrying}
          >
            {isRetrying ? (
              <Loader2 className='mr-2 h-4 w-4 animate-spin' />
            ) : (
              <RotateCcw className='mr-2 h-4 w-4' />
            )}
            Retry {failed} failed
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

interface LaneActions {
  captureIsPaused: boolean;
  retrying: string | null;
  onRetryFailed: (lane: ImporterLane) => void;
  busyCredentialLane: string | null;
  onPause: (laneKey: string) => void;
  onResume: (laneKey: string) => void;
  onResetBreaker: (laneKey: string) => void;
}

const LaneGrid: React.FC<{ lanes: ImporterLane[] } & LaneActions> = ({
  lanes,
  retrying,
  onRetryFailed,
  ...actions
}) => (
  <div className='grid gap-4 md:grid-cols-2 xl:grid-cols-3'>
    {lanes.map(lane => (
      <LaneCard
        key={lane.queue_key}
        lane={lane}
        onRetryFailed={() => onRetryFailed(lane)}
        isRetrying={retrying === lane.queue_key}
        {...actions}
      />
    ))}
  </div>
);

type NoteOutcome = 'Collapsed' | 'Rejected';

/**
 * The one choice an operator makes about how a roster's charts come across.
 * The sixty-day rule is the automatic one; the other two force one kind of
 * print on every row of the upload. Claims, falls and infections come with
 * every mode, so nothing here mentions them.
 */
const PRINT_MODES: {
  value: ImporterRosterPrintMode;
  label: string;
  detail: string;
}[] = [
  {
    value: 'auto',
    label: 'Automatic',
    detail:
      'One chart PDF per episode when the last episode ended over 60 days ago; a PDF per task otherwise.',
  },
  {
    value: 'task',
    label: 'One PDF per task',
    detail: 'Every documented task is printed to its own PDF.',
  },
  {
    value: 'episode',
    label: 'One chart PDF per episode',
    detail: 'Each episode is printed as one chart PDF.',
  },
];

interface OutcomeNote extends ImporterRosterRowNote {
  outcome: NoteOutcome;
}

/**
 * Every page of one roster, added up.
 *
 * The server queues the file a page at a time, so the totals a reader needs —
 * how much of the file was queued, how much collapsed, how much was refused —
 * exist only here. `rowsProcessed` is what the pages actually read, and it
 * reaches `rowsInFile` only if every page came back.
 */
interface RosterProgress {
  sourceAgencyId: string;
  sheets: ImporterRosterResult['sheets'];
  rowsInFile: number;
  rowsProcessed: number;
  enqueued: number;
  duplicates: number;
  rejected: number;
  notes: OutcomeNote[];
  detailTruncated: boolean;
  sequencerKicked: boolean;
}

function startProgress(): RosterProgress {
  return {
    sourceAgencyId: '',
    sheets: [],
    rowsInFile: 0,
    rowsProcessed: 0,
    enqueued: 0,
    duplicates: 0,
    rejected: 0,
    notes: [],
    detailTruncated: false,
    sequencerKicked: false,
  };
}

function addPage(
  progress: RosterProgress,
  page: ImporterRosterResult
): RosterProgress {
  const notes: OutcomeNote[] = [
    ...page.duplicate_rows.map(note => ({
      ...note,
      outcome: 'Collapsed' as const,
    })),
    ...page.rejected_rows.map(note => ({
      ...note,
      outcome: 'Rejected' as const,
    })),
  ].sort((a, b) => a.row_number - b.row_number);
  return {
    sourceAgencyId: page.source_agency_id,
    sheets: page.sheets,
    rowsInFile: page.rows_in_file,
    rowsProcessed: progress.rowsProcessed + page.rows_processed,
    enqueued: progress.enqueued + page.enqueued,
    duplicates: progress.duplicates + page.duplicates,
    rejected: progress.rejected + page.rejected,
    notes: [...progress.notes, ...notes],
    detailTruncated: progress.detailTruncated || page.detail_truncated,
    sequencerKicked: progress.sequencerKicked || page.sequencer_kicked,
  };
}

function csvCell(value: string | number | null): string {
  if (value === null) return '';
  return `"${String(value).replace(/"/g, '""')}"`;
}

/**
 * The rows that did not become queue rows, as a file the operator can work
 * through. Sheet, row and reason only — the roster on their own disk is where
 * the patient is, and this list never becomes a second copy of it.
 */
function notesCsv(notes: OutcomeNote[]): string {
  const header = 'Sheet,Row,Outcome,Collapsed onto row,Reason';
  const lines = notes.map(note =>
    [
      csvCell(note.sheet),
      csvCell(note.row_number),
      csvCell(note.outcome),
      csvCell(note.collapsed_onto_row),
      csvCell(note.reason),
    ].join(',')
  );
  return [header, ...lines].join('\n');
}

/**
 * The status report's four numbers as one sentence.
 *
 * "Missing on chart" and "not imported" are kept apart because they are fixed
 * in different places: one patient needs their episode captured again, the
 * other never got a chart at all. The three do not have to add up to the row
 * count — a row whose end date Roger could not read is none of them — so the
 * sentence never claims they do.
 */
function statusReportSummary(report: ImporterRosterStatusReport): string {
  const count = (value: number) => value.toLocaleString();
  return (
    `${count(report.rows)} rows: ${count(report.landed)} landed, ` +
    `${count(report.missing)} missing on chart, ` +
    `${count(report.notImported)} not imported`
  );
}

function downloadNotes(notes: OutcomeNote[]): void {
  const url = URL.createObjectURL(
    new Blob([notesCsv(notes)], { type: 'text/csv' })
  );
  const link = document.createElement('a');
  link.href = url;
  link.download = 'roster-rows-not-queued.csv';
  link.click();
  URL.revokeObjectURL(url);
}

const OutcomeTile: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div>
    <dt className='text-sm text-muted-foreground'>{label}</dt>
    <dd className='text-2xl font-semibold'>{children}</dd>
  </div>
);

const RosterOutcome: React.FC<{ result: RosterProgress }> = ({ result }) => {
  const notes = result.notes;
  const unread = result.sheets.filter(sheet => !sheet.read);
  return (
    <div className='space-y-4' data-testid='roster-outcome'>
      <dl className='grid grid-cols-2 gap-3 sm:grid-cols-4'>
        <OutcomeTile label='Rows read'>
          {result.rowsProcessed}
          {result.rowsProcessed !== result.rowsInFile && (
            <span className='text-base font-normal text-muted-foreground'>
              {' '}
              of {result.rowsInFile}
            </span>
          )}
        </OutcomeTile>
        <OutcomeTile label='Queued'>{result.enqueued}</OutcomeTile>
        <OutcomeTile label='Collapsed'>{result.duplicates}</OutcomeTile>
        <OutcomeTile label='Rejected'>{result.rejected}</OutcomeTile>
      </dl>

      <p className='text-sm text-muted-foreground'>
        Queued into {result.sourceAgencyId}. The roster lists one row per
        episode and a patient is imported once, so a patient's later episodes
        collapse instead of adding a chart.
      </p>

      {unread.length > 0 && (
        <Alert variant='destructive'>
          <AlertTriangle className='h-4 w-4' />
          <AlertTitle>Some sheets were not read</AlertTitle>
          <AlertDescription>
            {unread
              .map(sheet => `${sheet.name} (${sheet.rows} rows)`)
              .join(', ')}{' '}
            — no Medical Record Key column, so nothing on them was queued.
          </AlertDescription>
        </Alert>
      )}

      {result.enqueued > 0 && !result.sequencerKicked && (
        <p className='text-sm text-muted-foreground'>
          Importing starts within 5 minutes.
        </p>
      )}

      {notes.length > 0 && (
        <Collapsible className='space-y-2'>
          <div className='flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between'>
            <CollapsibleTrigger className='flex items-center gap-1 text-left text-sm font-medium'>
              <ChevronDown
                className='h-3.5 w-3.5 shrink-0'
                aria-hidden='true'
              />
              {notes.length} rows added no chart
            </CollapsibleTrigger>
            <Button
              variant='outline'
              size='sm'
              onClick={() => downloadNotes(notes)}
            >
              Download all (CSV)
            </Button>
          </div>
          <CollapsibleContent className='space-y-2'>
            {result.detailTruncated && (
              <p className='text-sm text-muted-foreground'>
                The counts above are complete; this list was cut short.
              </p>
            )}
            {notes.length > VISIBLE_NOTES && (
              <p className='text-sm text-muted-foreground'>
                Showing the first {VISIBLE_NOTES}. The CSV has all{' '}
                {notes.length}.
              </p>
            )}
            <div className='overflow-auto'>
              <Table className='min-w-[600px]'>
                <TableHeader>
                  <TableRow>
                    <TableHead>Sheet</TableHead>
                    <TableHead>Row</TableHead>
                    <TableHead>Outcome</TableHead>
                    <TableHead>Reason</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {notes.slice(0, VISIBLE_NOTES).map(note => (
                    <TableRow key={`${note.sheet}-${note.row_number}`}>
                      <TableCell className='text-muted-foreground'>
                        {note.sheet}
                      </TableCell>
                      <TableCell>{note.row_number}</TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            note.outcome === 'Rejected'
                              ? 'destructive'
                              : 'secondary'
                          }
                          className='whitespace-nowrap'
                        >
                          {note.outcome}
                        </Badge>
                      </TableCell>
                      <TableCell className='text-muted-foreground'>
                        {note.reason}
                        {note.collapsed_onto_row !== null &&
                          ` (row ${note.collapsed_onto_row})`}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
};

const QUEUE_COLUMNS = 10;

const QueueMessage: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <TableRow>
    <TableCell
      colSpan={QUEUE_COLUMNS}
      className='h-32 text-center text-sm text-muted-foreground'
    >
      {children}
    </TableCell>
  </TableRow>
);

const ImporterPage: React.FC = () => {
  // Responses held whole, newest page first. Empty until a load has
  // succeeded, which is also what tells a page that has never loaded apart
  // from one that loaded and found nothing.
  const [pages, setPages] = useState<ImporterQueue[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const [roster, setRoster] = useState<File | null>(null);
  const [emptyReferralPolicy, setEmptyReferralPolicy] = useState<
    EmptyReferralPolicy | ''
  >('');
  const [printMode, setPrintMode] = useState<ImporterRosterPrintMode>('auto');
  // Off every time the page loads. Overwrite deletes the charts it rebuilds,
  // so it is always something the operator ticked for this upload and never
  // something a remembered preference did for them.
  const [overwriteExisting, setOverwriteExisting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [rosterResult, setRosterResult] = useState<RosterProgress | null>(null);
  const [isDownloadingStatus, setIsDownloadingStatus] = useState(false);
  // The sentence shown beside the download, for the file that produced it.
  // Cleared when another file is chosen: a tally that outlived its roster
  // would describe a capture the operator is no longer looking at.
  const [statusSummary, setStatusSummary] = useState<string | null>(null);
  const [settings, setSettings] = useState<ImporterSettings | null>(null);
  const [hasSettingsFailed, setHasSettingsFailed] = useState(false);
  const [isSavingLanes, setIsSavingLanes] = useState(false);
  const [isSavingChartsPerLogin, setIsSavingChartsPerLogin] = useState(false);
  const [isTogglingCapture, setIsTogglingCapture] = useState(false);
  // The row id or the lane key currently retrying, so only that button spins
  // and a second press cannot race the first.
  const [retrying, setRetrying] = useState<string | null>(null);
  // The lane_key of the login a pause, resume or breaker reset is in flight
  // for. One at a time: these all reload the queue, and two overlapping presses
  // would each render the other's stale answer.
  const [busyCredentialLane, setBusyCredentialLane] = useState<string | null>(
    null
  );
  const fileInput = useRef<HTMLInputElement>(null);
  const showError = useStore(state => state.showError);
  const showSuccess = useStore(state => state.showSuccess);
  const hasPermission = useStore(state => state.hasPermission);
  // Reset and roster Overwrite are gated on the same slug by the same server
  // rule, so the page reads it once. A user without it never sees either.
  const isSuperUser = hasPermission(PermissionSlug.ADMIN_SUPER_USER);
  const [isResetting, setIsResetting] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [queueViewFilter, setQueueViewFilter] =
    useState<QueueViewFilter>('all');
  const queueStatusParam: ImporterQueueStatusFilter | undefined =
    queueViewFilter === 'all' ? undefined : queueViewFilter;
  // Bumped whenever the status filter changes. A read started under the old
  // filter — a poll, or a "Show older" still in flight — checks it when it
  // lands and throws its page away rather than writing another filter's rows
  // under the new label.
  const queueFilterGeneration = useRef(0);

  // `silent` is what the poll uses. It leaves the Refresh button alone — a
  // spinner appearing every 5 seconds reads as a page that cannot settle — and
  // it keeps a failing poll out of the toasts, where one message every 5
  // seconds would bury a refresh nobody asked for. The console still gets it,
  // and a load the operator did ask for still says so both ways.
  const load = useCallback(
    async (options: { silent?: boolean; resetPages?: boolean } = {}) => {
      if (!options.silent) setIsLoading(true);
      const generation = queueFilterGeneration.current;
      try {
        const head = await getImporterQueue({
          limit: QUEUE_PAGE_SIZE,
          status: queueStatusParam,
        });
        if (generation !== queueFilterGeneration.current) return;
        // A refresh replaces the first page and leaves the older ones the
        // operator asked for in place, so the poll cannot fold the list back
        // up under them.
        setPages(current =>
          options.resetPages ? [head] : [head, ...current.slice(1)]
        );
      } catch (error) {
        console.error('Failed to load the import queue:', error);
        if (!options.silent) showError(QUEUE_LOAD_FAILED);
      } finally {
        // A load the filter change superseded leaves the spinner to the one
        // that replaced it.
        if (!options.silent && generation === queueFilterGeneration.current) {
          setIsLoading(false);
        }
      }
    },
    [queueStatusParam, showError]
  );

  const loadSettings = useCallback(async () => {
    try {
      setSettings(await getImporterSettings());
      setHasSettingsFailed(false);
    } catch (error) {
      console.error('Failed to load the importer settings:', error);
      setHasSettingsFailed(true);
    }
  }, []);

  // Changing the filter starts the list over. Emptying the pages here is what
  // makes the reload a fresh first page: `load` depends on the filter, so the
  // polling effect re-runs and does the one fetch.
  const changeQueueViewFilter = useCallback((next: QueueViewFilter) => {
    queueFilterGeneration.current += 1;
    setPages([]);
    setQueueViewFilter(next);
  }, []);

  const refresh = useCallback(() => {
    void load();
    void loadSettings();
  }, [load, loadSettings]);

  useEffect(() => {
    refresh();

    let timer: ReturnType<typeof setInterval> | undefined;
    const schedule = () => {
      clearInterval(timer);
      timer = setInterval(
        () => void load({ silent: true }),
        document.visibilityState === 'visible'
          ? FOCUSED_REFRESH_INTERVAL_MS
          : HIDDEN_REFRESH_INTERVAL_MS
      );
    };
    const onVisibilityChange = () => {
      // Back on screen, and what is on it is up to a slow poll old.
      if (document.visibilityState === 'visible') void load({ silent: true });
      schedule();
    };

    schedule();
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [load, refresh]);

  const resetAgency = useCallback(async () => {
    setIsResetting(true);
    const totals: ImporterResetResult = {
      canceled_jobs: 0,
      queue_rows_deleted: 0,
      patients_deleted: 0,
      patients_remaining: 0,
      patients_protected: 0,
      done: false,
      stalled: false,
      restore_capture_pause: false,
    };
    try {
      let done = false;
      let restoreCapturePause: boolean | undefined;
      while (!done) {
        const page = await resetAgencyImport(
          restoreCapturePause === undefined
            ? {}
            : { restore_capture_pause: restoreCapturePause }
        );
        restoreCapturePause = page.restore_capture_pause;
        totals.canceled_jobs += page.canceled_jobs;
        totals.queue_rows_deleted += page.queue_rows_deleted;
        totals.patients_deleted += page.patients_deleted;
        totals.patients_remaining = page.patients_remaining;
        totals.patients_protected = page.patients_protected;
        totals.restore_capture_pause = page.restore_capture_pause;
        if (page.stalled) {
          totals.stalled = true;
          showError(
            'Reset paused',
            'Import jobs could not be confirmed stopped. Try again in a moment.'
          );
          return;
        }
        done = page.done;
      }
      totals.done = true;
      showSuccess(
        totals.patients_deleted +
          totals.canceled_jobs +
          totals.queue_rows_deleted ===
          0
          ? 'Nothing to reset'
          : 'Import reset',
        resetSummary(totals)
      );
      setPages([]);
      await load();
    } catch (error) {
      console.error('Failed to reset the agency import:', error);
      showError(getUserFacingApiMessage(error, AGENCY_RESET_FAILED));
    } finally {
      setIsResetting(false);
      setConfirmReset(false);
    }
  }, [load, showError, showSuccess]);

  const loadOlder = useCallback(async () => {
    const cursor = pages[pages.length - 1]?.next_before ?? null;
    if (cursor === null) return;
    setIsLoadingOlder(true);
    const generation = queueFilterGeneration.current;
    try {
      const older = await getImporterQueue({
        limit: QUEUE_PAGE_SIZE,
        before: cursor,
        status: queueStatusParam,
      });
      if (generation !== queueFilterGeneration.current) return;
      setPages(current => [...current, older]);
    } catch (error) {
      console.error('Failed to load older import rows:', error);
      showError(QUEUE_LOAD_FAILED);
    } finally {
      setIsLoadingOlder(false);
    }
  }, [pages, queueStatusParam, showError]);

  // The page polls every 30 seconds, which is far too long to sit looking at a
  // row that was just reopened, so both retries reload before they finish.
  const retryRow = useCallback(
    async (row: ImporterQueueRow) => {
      setRetrying(row.id);
      try {
        await retryImporterQueueRow(row.id);
        showSuccess('Retried', `${patientRef(row)} is back in the queue.`);
        if (queueStatusParam !== undefined) {
          // A filtered list has no live rows, so no fresh copy of the reopened
          // chart arrives to replace the failed one on an older page. Take it
          // out here, and the operator keeps their place in the list.
          setPages(current =>
            current.map(page => ({
              ...page,
              items: page.items.filter(item => item.id !== row.id),
            }))
          );
        }
        await load();
      } catch (error) {
        console.error('Failed to retry an import:', error);
        showError(getUserFacingApiMessage(error, ROW_RETRY_FAILED));
      } finally {
        setRetrying(null);
      }
    },
    [load, queueStatusParam, showError, showSuccess]
  );

  const retryLane = useCallback(
    async (lane: ImporterLane) => {
      setRetrying(lane.queue_key);
      try {
        const result = await retryImporterLaneFailures(lane.queue_key);
        showSuccess(
          result.retried > 0 ? 'Retried' : 'Nothing to retry',
          retrySummary(result)
        );
        // A filtered list would keep the reopened failures on its older pages,
        // since nothing on the first page replaces them, so start it over.
        await load({ resetPages: queueStatusParam !== undefined });
      } catch (error) {
        console.error('Failed to retry a lane:', error);
        showError(getUserFacingApiMessage(error, LANE_RETRY_FAILED));
      } finally {
        setRetrying(null);
      }
    },
    [load, queueStatusParam, showError, showSuccess]
  );

  // One runner for the three presses, because they differ only in the call and
  // the sentence — and because the part worth getting right is the same for
  // all three: reload afterwards so the card shows the server's answer rather
  // than an optimistic guess about a lane a sequencer is also writing to.
  const controlLane = useCallback(
    async (
      laneKey: string,
      act: (laneKey: string) => Promise<ImporterCredentialLane>,
      title: string,
      describe: (lane: ImporterCredentialLane) => string,
      fallback: string
    ) => {
      setBusyCredentialLane(laneKey);
      try {
        const lane = await act(laneKey);
        showSuccess(title, describe(lane));
        await load();
      } catch (error) {
        console.error('Failed to change an importer lane:', error);
        showError(getUserFacingApiMessage(error, fallback));
      } finally {
        setBusyCredentialLane(null);
      }
    },
    [load, showError, showSuccess]
  );

  const pauseLane = useCallback(
    (laneKey: string) =>
      void controlLane(
        laneKey,
        pauseImporterLane,
        'Login paused',
        lane =>
          lane.running > 0
            ? `${credentialLaneName(lane)} finishes the chart it is holding, then stops.`
            : `${credentialLaneName(lane)} will not claim another chart.`,
        LANE_PAUSE_FAILED
      ),
    [controlLane]
  );

  const resumeLane = useCallback(
    (laneKey: string) =>
      void controlLane(
        laneKey,
        resumeImporterLane,
        'Login resumed',
        lane =>
          // Resuming does not close a breaker, so a lane the source EMR is
          // still refusing has to say it is stopped for the other reason
          // rather than let the operator read "resumed" as "importing".
          lane.breaker.is_open
            ? `${credentialLaneName(lane)} is still refused by the source EMR, so it stays stopped.`
            : `${credentialLaneName(lane)} starts importing within 5 minutes.`,
        LANE_RESUME_FAILED
      ),
    [controlLane]
  );

  const resetLaneBreaker = useCallback(
    (laneKey: string) =>
      void controlLane(
        laneKey,
        resetImporterLaneBreaker,
        'Trying this login again',
        lane =>
          lane.is_paused
            ? `${credentialLaneName(lane)} is no longer refused, but it is still paused — resume it to import.`
            : `${credentialLaneName(lane)} takes the next chart. If it is still refused, it stops again.`,
        LANE_RESET_FAILED
      ),
    [controlLane]
  );

  const changeChartsPerLogin = useCallback(
    async (charts: number) => {
      const before = settings?.charts_per_login;
      if (before === undefined) return;
      setIsSavingChartsPerLogin(true);
      try {
        setSettings(await setImporterChartsPerLogin(charts));
        const each = charts === 1 ? 'one chart' : `up to ${charts} charts`;
        showSuccess(
          charts > before
            ? 'Charts per login raised'
            : 'Charts per login lowered',
          charts > before
            ? `Each login runs ${each} at once. The extra charts start within 5 minutes.`
            : `Each login runs ${each} at once. Charts already importing finish first.`
        );
      } catch (error) {
        console.error('Failed to change charts per login:', error);
        showError(getUserFacingApiMessage(error, CHARTS_PER_LOGIN_SAVE_FAILED));
      } finally {
        setIsSavingChartsPerLogin(false);
      }
    },
    [settings?.charts_per_login, showError, showSuccess]
  );

  const changeLaneCount = useCallback(
    async (lanes: number) => {
      const before = settings?.max_concurrent_lanes;
      if (before === undefined) return;
      setIsSavingLanes(true);
      try {
        setSettings(await setImporterMaxConcurrentLanes(lanes));
        showSuccess(
          lanes > before ? 'Logins raised' : 'Logins lowered',
          lanes > before
            ? `Up to ${lanes} at once. The extra logins start within 5 minutes.`
            : `Up to ${lanes} at once. The extra logins finish the chart they are holding, then stop.`
        );
      } catch (error) {
        console.error('Failed to change the importer lane count:', error);
        showError(getUserFacingApiMessage(error, LANE_COUNT_SAVE_FAILED));
      } finally {
        setIsSavingLanes(false);
      }
    },
    [settings, showError, showSuccess]
  );

  const toggleCapturePause = useCallback(async () => {
    if (settings === null || isResetting) return;
    const pause = settings.is_paused !== true;
    setIsTogglingCapture(true);
    try {
      setSettings(await setImporterCapturePaused(pause));
      showSuccess(
        pause ? 'Import paused' : 'Import resumed',
        pause
          ? 'Queued charts wait. Nothing already importing is stopped.'
          : 'Queued charts start importing now.'
      );
      await load();
    } catch (error) {
      console.error('Failed to change the importer capture pause:', error);
      showError(
        getUserFacingApiMessage(
          error,
          pause ? CAPTURE_PAUSE_FAILED : CAPTURE_RESUME_FAILED
        )
      );
    } finally {
      setIsTogglingCapture(false);
    }
  }, [isResetting, load, settings, showError, showSuccess]);

  const upload = useCallback(async () => {
    if (!roster || !emptyReferralPolicy) return;
    setIsUploading(true);
    setRosterResult(null);
    // The server queues a bounded number of rows per request, so a long roster
    // is several requests. Each page is shown as it lands: a 24,000-row capture
    // takes minutes, and an operator watching a spinner cannot tell a slow
    // upload from a stuck one. A page that fails stops the walk with what
    // landed still on screen — re-uploading the same file reports the rest.
    let progress = startProgress();
    let offset: number | null = 0;
    try {
      while (offset !== null) {
        const page = await uploadImporterRosterPage(
          roster,
          offset,
          printMode,
          overwriteExisting,
          emptyReferralPolicy
        );
        progress = addPage(progress, page);
        setRosterResult(progress);
        offset = page.next_offset;
      }
      setRoster(null);
      if (fileInput.current) fileInput.current.value = '';
    } catch (error) {
      console.error('Failed to queue the roster:', error);
      showError(getUserFacingApiMessage(error, ROSTER_UPLOAD_FAILED));
    } finally {
      setIsUploading(false);
      await load();
    }
  }, [
    load,
    overwriteExisting,
    printMode,
    emptyReferralPolicy,
    roster,
    showError,
  ]);

  const downloadStatusReport = useCallback(async () => {
    if (!roster) return;
    setIsDownloadingStatus(true);
    setStatusSummary(null);
    try {
      const report = await downloadImporterRosterStatus(roster);
      downloadBlobAsFile(report.blob, report.filename);
      setStatusSummary(statusReportSummary(report));
    } catch (error) {
      console.error('Failed to build the roster status report:', error);
      // The async reader: this request answers with a blob, so an error body
      // arrives as one too and the synchronous helper cannot read it.
      showError(
        await getUserFacingApiMessageAsync(error, ROSTER_STATUS_FAILED)
      );
    } finally {
      setIsDownloadingStatus(false);
    }
  }, [roster, showError]);

  const head = pages[0] ?? null;
  const rows = pagedRows(pages);
  const olderCursor = pages[pages.length - 1]?.next_before ?? null;
  // The server pages the live rows as well as the settled ones, so the table is
  // a window onto both halves. The lane counts are aggregated over every row,
  // which makes them the only honest total to compare against.
  const liveShown = rows.filter(row => LIVE_STATUSES.has(row.status)).length;
  const liveTotal = (head?.lanes ?? []).reduce(
    (total, lane) => total + lane.counts.queued + lane.counts.running,
    0
  );
  const failedTotal = (head?.lanes ?? []).reduce(
    (total, lane) => total + lane.counts.failed,
    0
  );
  const completedTotal = (head?.lanes ?? []).reduce(
    (total, lane) => total + lane.counts.completed,
    0
  );
  const liveTruncated = queueViewFilter === 'all' && liveTotal > liveShown;
  const queueFilterMeta = QUEUE_VIEW_FILTERS.find(
    filter => filter.value === queueViewFilter
  );
  const isFirstLoad = isLoading && head === null;
  // Sort is stable, so lanes of equal rank keep the order the server sends.
  const lanes = [...(head?.lanes ?? [])].sort(
    (a, b) => laneRank(a) - laneRank(b)
  );
  const refusedLogins: RefusedLogin[] = lanes.flatMap(lane =>
    lane.credential_lanes
      .filter(credential => credential.breaker.is_open)
      .map(credential => ({ pool: laneLabel(lane), credential }))
  );
  // Stopped lanes rank first, so they are always among the cards on screen —
  // and the banner above names every refused login either way.
  const shownLanes = lanes.slice(0, VISIBLE_LANES);
  const foldedLanes = lanes.slice(VISIBLE_LANES);
  // Every row names its login the same way the cards do, so the two read as
  // one page rather than two lists of different-looking names. Two maps
  // because a row is named two different ways: by its own login once it has
  // one, and by its pool while it is still waiting for one.
  const laneNames = new Map(
    lanes.map(lane => [lane.queue_key, laneLabel(lane)])
  );
  const loginNames = new Map(
    lanes.flatMap(lane =>
      lane.credential_lanes
        .filter(credential => credential.credential_id !== null)
        .map(credential => [
          credential.credential_id as string,
          credentialLaneName(credential),
        ])
    )
  );

  return (
    <div className='flex h-full flex-col'>
      <div className='flex flex-col gap-3 border-b px-6 py-4 sm:flex-row sm:items-center sm:justify-between'>
        <div>
          <h1 className='text-lg font-semibold'>Importer</h1>
          <p className='text-sm text-muted-foreground'>
            Charts from the old EMR.
          </p>
        </div>
        <Button
          variant='outline'
          size='sm'
          onClick={refresh}
          disabled={isLoading}
        >
          {isLoading ? (
            <Loader2 className='mr-2 h-4 w-4 animate-spin' />
          ) : (
            <RefreshCw className='mr-2 h-4 w-4' />
          )}
          Refresh
        </Button>
      </div>

      <div className='flex-1 space-y-6 overflow-auto px-6 py-4'>
        {refusedLogins.length > 0 && <RefusedLogins logins={refusedLogins} />}

        <LaneCount
          settings={settings}
          hasFailed={hasSettingsFailed}
          isSaving={isSavingLanes}
          isTogglingPause={isTogglingCapture}
          isResetting={isResetting}
          onSave={lanes => void changeLaneCount(lanes)}
          onTogglePause={() => void toggleCapturePause()}
        />

        <ChartsPerLogin
          settings={settings}
          hasFailed={hasSettingsFailed}
          isSaving={isSavingChartsPerLogin}
          onSave={charts => void changeChartsPerLogin(charts)}
        />

        {isFirstLoad ? (
          <div className='grid gap-4 md:grid-cols-2 xl:grid-cols-3'>
            {[0, 1, 2].map(index => (
              <Skeleton key={index} className='h-52 w-full rounded-xl' />
            ))}
          </div>
        ) : (
          lanes.length > 0 && (
            <div className='space-y-4'>
              <LaneGrid
                lanes={shownLanes}
                captureIsPaused={settings?.is_paused === true}
                retrying={retrying}
                onRetryFailed={lane => void retryLane(lane)}
                busyCredentialLane={busyCredentialLane}
                onPause={pauseLane}
                onResume={resumeLane}
                onResetBreaker={resetLaneBreaker}
              />
              {foldedLanes.length > 0 && (
                <Collapsible className='space-y-4'>
                  <CollapsibleTrigger className='group flex items-center gap-1 text-sm font-medium'>
                    <ChevronDown
                      className='h-3.5 w-3.5 shrink-0 transition-transform group-data-[state=open]:rotate-180'
                      aria-hidden='true'
                    />
                    {foldedLanes.length} more logins
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <LaneGrid
                      lanes={foldedLanes}
                      captureIsPaused={settings?.is_paused === true}
                      retrying={retrying}
                      onRetryFailed={lane => void retryLane(lane)}
                      busyCredentialLane={busyCredentialLane}
                      onPause={pauseLane}
                      onResume={resumeLane}
                      onResetBreaker={resetLaneBreaker}
                    />
                  </CollapsibleContent>
                </Collapsible>
              )}
            </div>
          )
        )}

        <Card>
          <CardHeader>
            <div className='flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between'>
              <div className='space-y-1.5'>
                <CardTitle>Queue</CardTitle>
                <CardDescription>
                  {queueFilterMeta?.description ??
                    'Queued and importing first, then the charts that finished most recently.'}
                </CardDescription>
              </div>
              <div className='w-full space-y-1 sm:w-56'>
                <Label htmlFor='importer-queue-filter' className='text-sm'>
                  Show
                </Label>
                <Select
                  value={queueViewFilter}
                  onValueChange={value =>
                    changeQueueViewFilter(value as QueueViewFilter)
                  }
                  disabled={isLoading && head === null}
                >
                  <SelectTrigger
                    id='importer-queue-filter'
                    className='w-full'
                    selectSize='sm'
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {QUEUE_VIEW_FILTERS.map(filter => (
                      <SelectItem key={filter.value} value={filter.value}>
                        {filter.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className='overflow-auto'>
              <Table className='min-w-[1240px]'>
                <TableHeader>
                  <TableRow>
                    <TableHead>Status</TableHead>
                    <TableHead>Patient</TableHead>
                    <TableHead>Login</TableHead>
                    {/* When the roster put this chart in the queue. Named for
                        the date it holds, so it is not read as the status
                        badge of the same name two columns to the left. */}
                    <TableHead className='whitespace-nowrap'>
                      Queued on
                    </TableHead>
                    <TableHead className='whitespace-nowrap'>Started</TableHead>
                    {/* Settle stamp, not last-change. Live rows send null. */}
                    <TableHead className='whitespace-nowrap'>Ended</TableHead>
                    {/* Ended minus Started: the chart's own cost, not the
                        wait behind the rest of the capture. */}
                    <TableHead className='whitespace-nowrap'>
                      Time Elapsed
                    </TableHead>
                    <TableHead className='whitespace-nowrap'>
                      Next try
                    </TableHead>
                    <TableHead>Outcome / error</TableHead>
                    <TableHead className='text-right'>Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isFirstLoad ? (
                    <QueueMessage>
                      <Loader2 className='mx-auto h-6 w-6 animate-spin' />
                    </QueueMessage>
                  ) : head === null ? (
                    <QueueMessage>
                      Could not load the queue. Press Refresh to try again.
                    </QueueMessage>
                  ) : rows.length === 0 ? (
                    <QueueMessage>
                      {queueViewFilter === 'failed'
                        ? 'No failed charts.'
                        : queueViewFilter === 'completed'
                          ? 'No imported charts yet.'
                          : 'No charts queued yet. Upload a roster below to start.'}
                    </QueueMessage>
                  ) : (
                    rows.map(row => {
                      const status =
                        row.status === 'completed' &&
                        row.import_outcome === 'skipped'
                          ? { label: 'Skipped', variant: 'outline' as const }
                          : row.status === 'completed' &&
                              row.import_outcome === 'patient_only'
                            ? {
                                label: 'Patient record only',
                                variant: 'outline' as const,
                              }
                            : row.status === 'completed' && row.duplicate_import
                              ? {
                                  label: 'Duplicate import',
                                  variant: 'outline' as const,
                                }
                              : row.status === 'completed' &&
                                  row.import_outcome === 'print_retry'
                                ? {
                                    label: 'Prints retried',
                                    variant: 'outline' as const,
                                  }
                                : STATUS_LABELS[row.status];
                      return (
                        <TableRow key={row.id}>
                          <TableCell>
                            <Badge
                              variant={status?.variant ?? 'outline'}
                              className='whitespace-nowrap'
                            >
                              {status?.label ?? row.status}
                            </Badge>
                          </TableCell>
                          <TableCell>{patientRef(row)}</TableCell>
                          {/* A credential id is long enough to wrap every row
                              onto two lines, which doubles the height of the
                              one table an operator scrolls most. */}
                          <TableCell className='whitespace-nowrap text-muted-foreground'>
                            {rowLogin(row, laneNames, loginNames) ??
                              row.source_agency_id}
                          </TableCell>
                          <TableCell className='whitespace-nowrap'>
                            {formatDateTimeInPT(row.created_at)}
                          </TableCell>
                          <TableCell className='whitespace-nowrap'>
                            {row.claimed_at
                              ? formatDateTimeInPT(row.claimed_at)
                              : '—'}
                          </TableCell>
                          <TableCell className='whitespace-nowrap'>
                            {row.ended_at
                              ? formatDateTimeInPT(row.ended_at)
                              : '—'}
                          </TableCell>
                          <TableCell className='whitespace-nowrap'>
                            {elapsedLabel(row)}
                          </TableCell>
                          <TableCell>
                            {row.not_before
                              ? formatDateTimeInPT(row.not_before)
                              : '—'}
                          </TableCell>
                          {/* One 270-character error makes a row taller than
                              a phone screen, so the cell shows the first
                              lines and hover carries the rest. */}
                          <TableCell className='text-muted-foreground'>
                            {row.status === 'completed' &&
                            row.import_summary?.length ? (
                              <details>
                                <summary>Import outcome</summary>
                                <ul className='max-w-sm space-y-1'>
                                  {row.import_summary.map((note, i) => (
                                    <li key={i}>{note}</li>
                                  ))}
                                </ul>
                              </details>
                            ) : row.error ? (
                              <span
                                className='line-clamp-2 max-w-sm'
                                title={row.error}
                              >
                                {row.error}
                              </span>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          <TableCell className='text-right'>
                            {row.status === 'failed' && (
                              <Button
                                variant='outline'
                                size='sm'
                                onClick={() => void retryRow(row)}
                                disabled={retrying === row.id}
                                aria-label={`Retry ${patientRef(row)}`}
                              >
                                {retrying === row.id ? (
                                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                                ) : (
                                  <RotateCcw className='mr-2 h-4 w-4' />
                                )}
                                Retry
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
            {rows.length > 0 && (
              <div className='mt-4 flex flex-col gap-3 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between'>
                <span>
                  {/* Never "all" while the live half is cut short. `olderCursor`
                      only walks the settled rows, so on its own it would call a
                      window onto 17,000 waiting charts a complete list. */}
                  {olderCursor === null && !liveTruncated
                    ? 'Showing all '
                    : 'Showing '}
                  {rows.length} {rows.length === 1 ? 'chart' : 'charts'}
                  {queueViewFilter === 'failed' &&
                    failedTotal > rows.length &&
                    ` — ${rows.length} of ${failedTotal} failed`}
                  {queueViewFilter === 'completed' &&
                    completedTotal > rows.length &&
                    ` — ${rows.length} of ${completedTotal} imported`}
                  {liveTruncated &&
                    ` — the ${liveShown} oldest of ${liveTotal} still to import`}
                </span>
                {olderCursor !== null && (
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={() => void loadOlder()}
                    disabled={isLoadingOlder}
                  >
                    {isLoadingOlder ? (
                      <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                    ) : (
                      <ChevronDown className='mr-2 h-4 w-4' />
                    )}
                    Show older
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Queue a roster</CardTitle>
            <CardDescription>
              Upload the episode roster from the old EMR (.xls or .xlsx). Every
              patient it names is queued.
            </CardDescription>
          </CardHeader>
          <CardContent className='space-y-4'>
            <div className='space-y-2'>
              <Label id='roster-print-mode-label' className='text-sm'>
                Print mode
              </Label>
              <RadioGroup
                value={printMode}
                onValueChange={value =>
                  setPrintMode(value as ImporterRosterPrintMode)
                }
                disabled={isUploading}
                aria-labelledby='roster-print-mode-label'
                className='flex flex-col gap-2 sm:flex-row sm:gap-6'
              >
                {PRINT_MODES.map(mode => (
                  <div key={mode.value} className='flex items-center gap-2'>
                    <RadioGroupItem
                      value={mode.value}
                      id={`roster-print-mode-${mode.value}`}
                    />
                    <Label
                      htmlFor={`roster-print-mode-${mode.value}`}
                      className='text-sm font-normal'
                    >
                      {mode.label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
              <p className='text-sm text-muted-foreground'>
                {PRINT_MODES.find(mode => mode.value === printMode)?.detail}
              </p>
            </div>
            <div className='space-y-2'>
              <Label htmlFor='empty-referral-policy'>
                Empty referral charts
              </Label>
              <Select
                value={emptyReferralPolicy}
                onValueChange={value =>
                  setEmptyReferralPolicy(value as EmptyReferralPolicy)
                }
                disabled={isUploading}
              >
                <SelectTrigger
                  id='empty-referral-policy'
                  className='w-full sm:w-72'
                  aria-describedby='empty-referral-policy-help'
                >
                  <SelectValue placeholder='Choose how to handle empty referrals' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='skip'>Skip</SelectItem>
                  <SelectItem value='patient_only'>
                    Import patient record only
                  </SelectItem>
                </SelectContent>
              </Select>
              <p
                id='empty-referral-policy-help'
                className='text-sm text-muted-foreground'
              >
                Applies only to verified empty, inactive referrals. Clinical and
                uncertain histories follow the normal import.
                {emptyReferralPolicy === 'skip' &&
                  ' Empty referrals will be skipped and reported.'}
                {emptyReferralPolicy === 'patient_only' &&
                  ' Retains demographics and source evidence without creating an admission or episode.'}
              </p>
            </div>

            {isSuperUser && (
              <div className='space-y-2'>
                <div className='flex items-center gap-2'>
                  <Checkbox
                    id='roster-overwrite-existing'
                    checked={overwriteExisting}
                    onCheckedChange={checked =>
                      setOverwriteExisting(checked === true)
                    }
                    disabled={isUploading}
                  />
                  <Label
                    htmlFor='roster-overwrite-existing'
                    className='text-sm font-normal'
                  >
                    Overwrite existing imported charts
                  </Label>
                </div>
                <p className='text-sm text-muted-foreground'>
                  Rebuilds charts this importer created that nobody has worked
                  on in Roger; anything with Roger work is refused with the
                  reason.
                </p>
              </div>
            )}
            <div className='flex flex-col gap-3 sm:flex-row sm:items-center'>
              <input
                ref={fileInput}
                type='file'
                accept='.xls,.xlsx'
                aria-label='Roster file'
                disabled={isUploading || isDownloadingStatus}
                onChange={event => {
                  setRoster(event.target.files?.[0] ?? null);
                  setStatusSummary(null);
                }}
                className='w-full text-sm sm:w-auto'
              />
              <Button
                size='sm'
                onClick={() => void upload()}
                disabled={
                  !roster ||
                  !emptyReferralPolicy ||
                  isUploading ||
                  isDownloadingStatus
                }
                className='sm:w-auto'
              >
                {isUploading ? (
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                ) : (
                  <Upload className='mr-2 h-4 w-4' />
                )}
                {isUploading ? 'Queueing…' : 'Queue roster'}
              </Button>
              <Button
                variant='outline'
                size='sm'
                onClick={() => void downloadStatusReport()}
                disabled={!roster || isUploading || isDownloadingStatus}
                className='sm:w-auto'
              >
                {isDownloadingStatus ? (
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                ) : (
                  <FileDown className='mr-2 h-4 w-4' />
                )}
                {isDownloadingStatus ? 'Building…' : 'Download status report'}
              </Button>
              {isUploading && rosterResult && (
                <span className='text-sm text-muted-foreground'>
                  {rosterResult.rowsProcessed} of {rosterResult.rowsInFile} rows
                </span>
              )}
            </div>
            <p className='text-sm text-muted-foreground'>
              The status report queues nothing. It hands this file back with
              what became of every row: the chart, its episodes, and the reason
              where there is none.
            </p>
            {statusSummary && (
              <p className='text-sm' data-testid='roster-status-summary'>
                {statusSummary}
              </p>
            )}
            {rosterResult && <RosterOutcome result={rosterResult} />}
          </CardContent>
        </Card>

        {isSuperUser && (
          <Card>
            <CardHeader>
              <CardTitle>Reset import</CardTitle>
              <CardDescription>
                Cancel every job in this agency&apos;s queue and delete imported
                patients that have no Roger work on the chart. Native patients
                and imported charts clinicians have worked in stay.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                variant='destructive'
                size='sm'
                onClick={() => setConfirmReset(true)}
                disabled={isResetting || isUploading}
              >
                {isResetting ? (
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                ) : (
                  <Trash2 className='mr-2 h-4 w-4' />
                )}
                {isResetting ? 'Resetting…' : 'Reset import'}
              </Button>
            </CardContent>
          </Card>
        )}

        <ConfirmationDialog
          open={confirmReset}
          title="Reset this agency's import?"
          message='This cancels every import job and permanently deletes imported patients that still carry only importer data. Patients created in Roger and imported charts with clinician work stay. This cannot be undone.'
          confirmLabel='Reset this agency'
          cancelLabel='Cancel'
          loading={isResetting}
          onAccept={() => void resetAgency()}
          onCancel={() => {
            if (!isResetting) setConfirmReset(false);
          }}
        />
      </div>
    </div>
  );
};

// Gate before the page mounts, so a refused user never starts its poll.
const Importer: React.FC = () => {
  const hasPermission = useStore(state => state.hasPermission);

  if (!hasPermission(PermissionSlug.IMPORTER_ACCESS)) {
    return (
      <div className='container mx-auto px-4 py-6'>
        <Alert variant='destructive'>
          <AlertTriangle className='h-4 w-4' />
          <AlertTitle>You do not have access to the importer</AlertTitle>
          <AlertDescription>
            Bringing charts across from another EMR is run by Roger. Ask your
            administrator if you need it done for your agency.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return <ImporterPage />;
};

export default Importer;
